/**
 * Approbations (F2): the Power-Automate-style approvals inbox. "En attente"
 * lists /api/approvals/pending (same queryKey as the bell and the nav badge,
 * so the three views dedupe into one poll); each item opens the standard
 * ApprovalDialog and links to its run. "Historique" is a pointer to the run
 * detail pages — the backend has no decision-history listing endpoint yet,
 * and this page does not invent one.
 */
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { api, type PendingApproval } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { usePageTitle } from "../hooks/usePageTitle";
import { APPROVALS_POLL_MS, ID_SLICE_LEN } from "../lib/config";
import { PageHeader } from "../components/ui/PageHeader";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { EmptyState } from "../components/ui/EmptyState";
import { ApprovalDialog } from "../components/ApprovalDialog";

export function ApprovalsPage() {
  const { t } = useTranslation();
  usePageTitle("nav.approvals");
  const [tab, setTab] = useState<"pending" | "history">("pending");
  const [reviewing, setReviewing] = useState<PendingApproval | null>(null);

  const pendingQuery = useApiQuery(["approvals", "pending"], api.pending, {
    pollMs: APPROVALS_POLL_MS,
  });
  const pending = pendingQuery.data ?? null;

  return (
    <Stack spacing={3}>
      <PageHeader title={t("nav.approvals")} subtitle={t("approvals.subtitle")} />

      <Tabs value={tab} onChange={(_e, next: "pending" | "history") => setTab(next)}>
        <Tab value="pending" label={t("approvals.tabPending")} />
        <Tab value="history" label={t("approvals.tabHistory")} />
      </Tabs>

      {tab === "history" && (
        <Alert severity="info">
          {t("approvals.historyHint")}{" "}
          <Link component={RouterLink} to="/runs" underline="hover">
            {t("runs.title")}
          </Link>
        </Alert>
      )}

      {tab === "pending" && (
        <>
          {!pending && pendingQuery.isError && (
            <ErrorAlert
              error={pendingQuery.error}
              onRetry={() => void pendingQuery.refetch()}
            />
          )}
          {!pending && !pendingQuery.isError && <LinearProgress />}
          {pending && pending.length === 0 && (
            <EmptyState
              icon={<FactCheckIcon fontSize="large" />}
              message={t("approvals.emptyInbox")}
            />
          )}
          {pending && pending.length > 0 && (
            <Stack spacing={2}>
              {pending.map((item) => (
                <Card key={item.step_id}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={2}
                    >
                      <div>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {item.step_key} · {item.tool_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.dag} ·{" "}
                          <Link
                            component={RouterLink}
                            to={`/runs/${item.job_id}`}
                            underline="hover"
                            className="num"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.job_id.slice(0, ID_SLICE_LEN)}
                          </Link>
                          {item.rows_out > 0
                            ? ` · ${t("files.rows", { count: item.rows_out })}`
                            : ""}
                          {item.unmatched_rows > 0
                            ? ` · ${t("files.unmatchedShort", { count: item.unmatched_rows })}`
                            : ""}
                        </Typography>
                      </div>
                      <Stack direction="row" spacing={1} flexShrink={0}>
                        <Button
                          component={RouterLink}
                          to={`/runs/${item.job_id}`}
                          size="small"
                        >
                          {t("approvals.openRun")}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setReviewing(item)}
                        >
                          {t("approvals.review")}
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}

      {reviewing && (
        <ApprovalDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => void pendingQuery.refetch()}
        />
      )}
    </Stack>
  );
}
